import {
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    Switch,
    MenuItem,
    Select,
    Tooltip
} from "@material-ui/core";
import React from "react";
import styled from "styled-components/macro";
import {SectionHeading} from "../RecommendationWidget/RecommendationWidget";
import InputTextApollo from "../../inputs/InputTextApollo";
import {getNumberInputValue,} from "../../../utils/inputUtils";
import { AS_VERSION_OPTIONS, getCalculatorUrlForVersion } from "../../../constants/calculatorConstants";
import { isApAvailable } from "../../../adapters/apSupportAdapter";

const Container = styled.div`
  border: 1px solid var(--color-border);
  background: var(--color-background);
  border-radius: 3px;
  padding: 20px;
  margin-bottom: 20px;
`;

export const FlexSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const RecommendationBanner = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid #f0c674;
  background: #fff8e6;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b4a00;

  strong {
    color: #5a3d00;
  }

  ul {
    margin: 6px 0 0;
    padding-left: 20px;
  }
`;

const ApRecommendationBanner = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid #f0c674;
  background: #fff8e6;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b4a00;

  strong { color: #5a3d00; }
  ul { margin: 6px 0 0; padding-left: 20px; }
`;

const RECOMMENDED_MARK = " ★";


const HighLevelConfig = ({
                             highLevelParameters,
                             onChangeHighLevelParameters,
                         }: {
    highLevelParameters: HighLevelParametersInterface;
    onChangeHighLevelParameters: (data: HighLevelParametersInterface) => void;
}) => {
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name} = e.currentTarget;
        const dataCopy: any = {...highLevelParameters};
        dataCopy[name] = !dataCopy[name];
        onChangeHighLevelParameters(dataCopy);
    };

    // RKE2 flavour is a UI consolidation of the eval/lite/ha deployment types.
    // Internal deploymentType stays as "eval"/"lite"/"ha" so all downstream
    // calculation, filtering, and ?data= restoration logic is unchanged.
    const RKE2_DEPLOYMENT_TYPES = ["eval", "lite", "ha"] as const;
    const isRke2DeploymentType = (dt: string) =>
        (RKE2_DEPLOYMENT_TYPES as readonly string[]).includes(dt);

    const rke2ProfileFromDeploymentType: Record<string, string> = {
        eval: "default",
        lite: "lite",
        ha: "ha",
    };
    const deploymentTypeFromRke2Profile: Record<string, string> = {
        default: "eval",
        lite: "lite",
        ha: "ha",
    };

    const flavourValue = isRke2DeploymentType(highLevelParameters.deploymentType)
        ? "rke2"
        : highLevelParameters.deploymentType;

    const rke2ProfileValue =
        rke2ProfileFromDeploymentType[highLevelParameters.deploymentType] || "default";

    const handleProfileChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        // RKE2 has no direct deploymentType — map to "eval" (default profile).
        dataCopy.deploymentType = value === "rke2" ? "eval" : value;
        dataCopy.apMode = false;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleRke2ProfileChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.deploymentType = deploymentTypeFromRke2Profile[value] || "eval";
        dataCopy.apMode = false;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleFaultCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.faultCount = Math.max(0, +value);
        onChangeHighLevelParameters(dataCopy);
    };

    const handleCoreCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.coreCount = value === "" ? "" : +value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleCoreCountBlur = () => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.coreCount = Math.max(8, +(dataCopy.coreCount) || 8);
        onChangeHighLevelParameters(dataCopy);
    };

    const handleRamCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.ramCount = value === "" ? "" : +value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleRamCountBlur = () => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.ramCount = Math.max(16, +(dataCopy.ramCount) || 16);
        onChangeHighLevelParameters(dataCopy);
    };

    const handleHostChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.host = value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleExternalS3Switch = (e: React.ChangeEvent<any>) => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.externalS3 = !dataCopy.externalS3;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleBackupCheckbox = (e: React.ChangeEvent<any>) => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.backupEnabled = !dataCopy.backupEnabled;
        onChangeHighLevelParameters(dataCopy);
    }

    const handleMultiZoneCheckBox = (e: React.ChangeEvent<any>) => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.isMultizoanl = !dataCopy.isMultizoanl;
        onChangeHighLevelParameters(dataCopy);
    }

    const handleApModeCheckbox = (e: React.ChangeEvent<any>) => {
        const dataCopy: any = {...highLevelParameters};
        dataCopy.apMode = !dataCopy.apMode;
        onChangeHighLevelParameters(dataCopy);
    }

    const apAvailable = isApAvailable(
        highLevelParameters.deploymentType,
        highLevelParameters.version,
    );

    const handleAksProfileChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.aksProfile = value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleEksProfileChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.eksProfile = value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleOpenshiftProfileChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const dataCopy: any = {...highLevelParameters};
        dataCopy.openshiftProfile = value;
        onChangeHighLevelParameters(dataCopy);
    };

    const handleVersionChange = (e: React.ChangeEvent<any>) => {
        const value = e.target.value as string;

        if (value === highLevelParameters.version) return;

        const isKnownVersion = AS_VERSION_OPTIONS.some((opt) => opt.value === value);
        if (!isKnownVersion) return;

        // Don't carry ?data or #hash across versions — the encoded state is
        // version-specific (deployment types, products, property shapes differ)
        // and would silently corrupt the target version's calculator.
        window.location.assign(getCalculatorUrlForVersion(value));
    };

    return (
        <Container>
            <SectionHeading>Global Config</SectionHeading>
            <FlexSection>
                <div>
                    <InputLabel id="version-label">AS Version</InputLabel>
                    <FormControl fullWidth>
                        <Select
                            label="AS Version"
                            labelId="version-label"
                            value={highLevelParameters.version}
                            onChange={handleVersionChange}
                        >
                            {AS_VERSION_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div>
                    <Tooltip title="★ marks recommended deployment flavours (customer-managed K8s).">
                        <InputLabel id="profile-label">AS Flavour</InputLabel>
                    </Tooltip>
                    <FormControl fullWidth>
                        <Select
                            label="AS Flavour"
                            labelId="profile-label"
                            value={flavourValue}
                            onChange={handleProfileChange}
                        >
                            <MenuItem key={"air-eks"} value={"air-eks"}>Automation Suite - EKS{RECOMMENDED_MARK}</MenuItem>
                            <MenuItem key={"air-aks"} value={"air-aks"}>Automation Suite - AKS{RECOMMENDED_MARK}</MenuItem>
                            <MenuItem key={"air-openshift"} value={"air-openshift"}>Automation Suite - Openshift{RECOMMENDED_MARK}</MenuItem>
                            <MenuItem key={"rke2"} value={"rke2"}>Automation Suite - Linux</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                {isRke2DeploymentType(highLevelParameters.deploymentType) && (
                    <div>
                        <InputLabel id="rke2-profile-label">Profile</InputLabel>
                        <FormControl fullWidth>
                            <Select
                                label="Profile"
                                labelId="rke2-profile-label"
                                value={rke2ProfileValue}
                                onChange={handleRke2ProfileChange}
                            >
                                <MenuItem key={"default"} value={"default"}>Default</MenuItem>
                                <MenuItem key={"lite"} value={"lite"}>Lite</MenuItem>
                                <MenuItem key={"ha"} value={"ha"}>HA</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {highLevelParameters.deploymentType === "air-aks" && (
                    <div>
                        <InputLabel id="aks-profile-label">Profile</InputLabel>
                        <FormControl fullWidth>
                            <Select
                                label="Profile"
                                labelId="aks-profile-label"
                                value={highLevelParameters.aksProfile || "default"}
                                onChange={handleAksProfileChange}
                            >
                                <MenuItem key={"default"} value={"default"}>Default</MenuItem>
                                <MenuItem key={"lite"} value={"lite"}>Lite</MenuItem>
                                <MenuItem key={"ha"} value={"ha"}>HA</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {highLevelParameters.deploymentType === "air-eks" && (
                    <div>
                        <InputLabel id="eks-profile-label">Profile</InputLabel>
                        <FormControl fullWidth>
                            <Select
                                label="Profile"
                                labelId="eks-profile-label"
                                value={highLevelParameters.eksProfile || "default"}
                                onChange={handleEksProfileChange}
                            >
                                <MenuItem key={"default"} value={"default"}>Default</MenuItem>
                                <MenuItem key={"lite"} value={"lite"}>Lite</MenuItem>
                                <MenuItem key={"ha"} value={"ha"}>HA</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {highLevelParameters.deploymentType === "air-openshift" && (
                    <div>
                        <InputLabel id="openshift-profile-label">Profile</InputLabel>
                        <FormControl fullWidth>
                            <Select
                                label="Profile"
                                labelId="openshift-profile-label"
                                value={highLevelParameters.openshiftProfile || "default"}
                                onChange={handleOpenshiftProfileChange}
                            >
                                <MenuItem key={"default"} value={"default"}>Default</MenuItem>
                                <MenuItem key={"lite"} value={"lite"}>Lite</MenuItem>
                                <MenuItem key={"ha"} value={"ha"}>HA</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {(["air-aks", "air-eks"].includes(highLevelParameters.deploymentType)) && (
                    <div>
                        <Tooltip
                            title="Enable when deploying into an existing managed Kubernetes cluster that already provides the Service Fabric layer. The calculator will exclude Service Fabric sizing from the total.">
                            <InputLabel id="shared-cluster-label">Cluster</InputLabel>
                        </Tooltip>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!highLevelParameters.sharedCluster}
                                    onChange={handleCheckboxChange}
                                    name="sharedCluster"
                                />
                            }
                            label="Shared Cluster"
                        />
                    </div>
                )}
                {(["ha", "eval", "lite"].includes(highLevelParameters.deploymentType)) && (
                    <>
                        <div>
                            <InputLabel id="options-label">Options</InputLabel>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!highLevelParameters.airGapped}
                                        onChange={handleCheckboxChange}
                                        name="airGapped"
                                    />
                                }
                                label="Air Gapped Deployment"
                            />
                        </div>
                    </>
                )}
                {(["ha", "eval", "lite"].includes(highLevelParameters.deploymentType)) && (
                    <>
                        <div>
                            <InputLabel id="options-label">Datacenter</InputLabel>
                            <FormControl fullWidth>
                                <Select
                                    label="host"
                                    value={highLevelParameters.host}
                                    onChange={handleHostChange}
                                >
                                    <MenuItem key={"aws"} value={"AWS"}>AWS</MenuItem>
                                    <MenuItem key={"azure"} value={"Azure"}>Azure</MenuItem>
                                    <MenuItem key={"on-prem"} value={"On-Prem"}>Other Cloud/On-Prem</MenuItem>
                                </Select>
                                {highLevelParameters.host === "On-Prem" && (
                                    <FormControlLabel value="External S3"
                                                      control={
                                                          <Switch
                                                              checked={!!highLevelParameters.externalS3}
                                                              onChange={handleExternalS3Switch}
                                                          />
                                                      }
                                                      label="External S3" />
                                )}
                            </FormControl>
                        </div>
                    </>
                )}
                {(() => {
                    const isRke2DrType = ["eval", "ha", "lite"].includes(highLevelParameters.deploymentType);
                    const showDrSection = isRke2DrType || apAvailable;
                    if (!showDrSection) return null;
                    return (
                        <div>
                            <InputLabel id="options-label">Disaster Recovery</InputLabel>
                            {isRke2DrType && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!highLevelParameters.backupEnabled}
                                            onChange={handleBackupCheckbox}
                                        />
                                    }
                                    label="Enable Backup"
                                />
                            )}
                            {(["ha", "lite"].includes(highLevelParameters.deploymentType)) && (
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={!!highLevelParameters.isMultizoanl}
                                        onChange={handleMultiZoneCheckBox}/>} label="Multi Zonal"/>
                            )}
                            {apAvailable && (
                                <Tooltip title="Adds a paired secondary site for Active/Passive disaster recovery. Products unsupported on the secondary site are excluded from the secondary recommendation.">
                                    <FormControlLabel
                                        control={<Switch
                                            checked={!!highLevelParameters.apMode}
                                            onChange={handleApModeCheckbox}
                                            name="apMode"/>}
                                        label="Active/Passive"/>
                                </Tooltip>
                            )}
                        </div>
                    );
                })()}
                {(["ha", "air-aks", "air-eks", "lite", "air-openshift"].includes(highLevelParameters.deploymentType)) && (
                    <>
                        <div>
                            <Tooltip
                                title="Defines how many cluster nodes can go down without impacting the cluster (minimum 0).">
                                <InputLabel id="options-label">Node Fault Tolerance</InputLabel>
                            </Tooltip>
                            <InputTextApollo
                                //label="Faulty Nodes Tolerance"
                                variant="outlined"
                                inputProps={{type: "number"}}
                                InputProps={{
                                    name: "faultCount",
                                    value: getNumberInputValue(highLevelParameters.faultCount),
                                    onChange: handleFaultCountChange,
                                }}
                            />
                        </div>
                        <div>
                            <Tooltip
                                title="The number of vCPU a node has (minimum 8 vCPU).">
                                <InputLabel id="options-label">Node Core Count (vCPU)</InputLabel>
                            </Tooltip>
                            <InputTextApollo
                                variant="outlined"
                                inputProps={{type: "number"}}
                                InputProps={{
                                    name: "coreCount",
                                    value: getNumberInputValue(highLevelParameters.coreCount),
                                    onChange: handleCoreCountChange,
                                    onBlur: handleCoreCountBlur,
                                }}
                            />
                        </div>
                        <div>
                            <Tooltip
                                title="The amount of RAM in GiB a node has (minimum 16 GiB).">
                                <InputLabel id="options-label">Node RAM (GiB)</InputLabel>
                            </Tooltip>
                            <InputTextApollo
                                variant="outlined"
                                inputProps={{type: "number"}}
                                InputProps={{
                                    name: "ramCount",
                                    value: getNumberInputValue(highLevelParameters.ramCount),
                                    onChange: handleRamCountChange,
                                    onBlur: handleRamCountBlur,
                                }}
                            />
                        </div>
                    </>
                )}
            </FlexSection>
            {flavourValue === "rke2" && (
                <RecommendationBanner role="note">
                    <strong>Consider a managed Kubernetes deployment.</strong>
                    <ul>
                        <li>On Cloud? Use <strong>EKS</strong> (AWS) or <strong>AKS</strong> (Azure) ★</li>
                        <li>On-premise? Use <strong>OpenShift</strong> ★</li>
                        <li>No K8s available (air-gapped)? Stay on <strong>Linux</strong>. Requires expertise in Linux and K8s — AS bundles RKE2 (embedded K8s).</li>
                    </ul>
                    Managed K8s means leveraging existing infrastructure, simpler installation &amp; upgrades and faster security approvals.
                </RecommendationBanner>
            )}
            {apAvailable && !!highLevelParameters.apMode && (
                <ApRecommendationBanner role="note">
                    <strong>Active/Passive deployment requirements.</strong>
                    <ul>
                        <li>Both sites need similar compute capacity, review the Primary and Secondary recommendations below.</li>
                        <li>External data services must be <strong>replicated</strong>: SQL via Always-On Availability Group, object store via cross-region replication (CRR), and a separately provisioned cache (Redis/ElastiCache) per site.</li>
                        <li>Some products are not supported on the secondary site and will not function after failover - see the "Active/Passive Recommendations" in the Details section.</li>
                    </ul>
                </ApRecommendationBanner>
            )}
        </Container>
    );
};

export default HighLevelConfig;
